import {
	isListChange,
	listItemKey,
	type StoryEditChange,
	type StoryEditElementOwner,
	type StoryEditListAddress,
	type StoryEditSkip,
	type StoryEditSkipReason,
	type StoryEditTarget,
	type StoryListOperation,
	type StoryListChange,
	type StoryPropertyChange,
	type StoryPropertyPatch,
} from './types';

// Reference semantics of the story edit operation contract: how one action's changes apply to the
// document they were captured from. Strict: a missing target, item or anchor rejects the action.
// Tolerant: applies on top of concurrent changes and reports what it skipped.

type Model = Record<string, unknown>;

/** The parts of a story an action changes: story properties and the latest version. */
export type StoryEditDocument = { story: Model; settings: Model; data: Model };

export class StoryEditApplyError extends Error {}

const isRecord = (value: unknown): value is Model =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

type Policy = 'strict' | 'tolerant';

/** A skipped change or operation; the caller adds the change index. */
type Skip = Omit<StoryEditSkip, 'change'>;

/** Strict applying rejects at the first problem; tolerant applying reports it and goes on. */
function reject(policy: Policy, message: string) {
	if (policy === 'strict') throw new StoryEditApplyError(message);
}

/** Applies one action's changes, in order, to a copy of `document`. */
export function applyStoryEditChanges(document: StoryEditDocument, changes: StoryEditChange[]): StoryEditDocument {
	return applyChanges(document, changes, 'strict').document;
}

/**
 * Applies one action's changes on top of a document that may differ from the one they were captured from.
 * Never throws for a missing target, item or anchor; reports what it skipped instead.
 */
export function applyStoryEditChangesTolerantly(
	document: StoryEditDocument,
	changes: StoryEditChange[]
): { document: StoryEditDocument; skipped: StoryEditSkip[] } {
	return applyChanges(document, changes, 'tolerant');
}

function applyChanges(document: StoryEditDocument, changes: StoryEditChange[], policy: Policy) {
	const result = structuredClone(document);
	const skipped: StoryEditSkip[] = [];
	changes.forEach((change, index) => {
		const skips = isListChange(change)
			? applyListChange(result, change, policy)
			: applyPropertyChange(result, change, policy);
		skipped.push(...skips.map(skip => ({ change: index, ...skip })));
	});
	return { document: result, skipped };
}

const splitPointer = (path: string) =>
	path
		.slice(1)
		.split('/')
		.map(key => key.replace(/~1/g, '/').replace(/~0/g, '~'));

/** Applies the patch to `model`; returns false, with `model` possibly half-changed, when a parent is missing. */
function applyPatch(model: Model, patch: StoryPropertyPatch[], policy: Policy): boolean {
	for (const operation of patch) {
		const keys = splitPointer(operation.path);
		const last = keys.pop()!;
		let parent: unknown = model;
		for (const key of keys) parent = isRecord(parent) ? parent[key] : undefined;
		if (!isRecord(parent)) {
			reject(policy, `Missing parent of ${operation.path}`);
			return false;
		}
		if (operation.op === 'remove') delete parent[last];
		else parent[last] = structuredClone(operation.value);
	}
	return true;
}

function applyPropertyChange(
	document: StoryEditDocument,
	{ target, patch }: StoryPropertyChange,
	policy: Policy
): Skip[] {
	const model = resolveTarget(document, target);
	if (!model) {
		reject(policy, `Target ${JSON.stringify(target)} not found`);
		return [{ reason: 'target_missing' }];
	}
	if (policy === 'strict') {
		applyPatch(model, patch, policy);
		return [];
	}
	// A skipped property change leaves the target untouched, so the patch goes to a copy first.
	const draft = structuredClone(model);
	if (!applyPatch(draft, patch, policy)) return [{ reason: 'target_missing' }];
	for (const key of Object.keys(model)) delete model[key];
	Object.assign(model, draft);
	return [];
}

/** Whether an item with this `_id` exists anywhere in the story's document. */
function containsId(value: unknown, id: string): boolean {
	if (Array.isArray(value)) return value.some(item => containsId(item, id));
	if (!isRecord(value)) return false;
	return value._id === id || Object.values(value).some(member => containsId(member, id));
}

/** Whether inserting would duplicate an item: an `_id` is unique in the story, a reference in its list. */
function isExistingItem(document: StoryEditDocument, items: unknown[], value: unknown): boolean {
	const key = listItemKey(value);
	if (key === undefined) return false;
	if (items.some(item => listItemKey(item) === key)) return true;
	return isRecord(value) && value._id === key && containsId(document.data, key);
}

function applyListChange(
	document: StoryEditDocument,
	{ target, list, operations }: StoryListChange,
	policy: Policy
): Skip[] {
	const items = resolveList(document, { target, list });
	if (!items) {
		reject(policy, `${target.kind} has no ${list} list`);
		return operations.map((_, operation) => ({ operation, reason: 'container_missing' }));
	}
	const skipped: Skip[] = [];
	operations.forEach((operation, index) => {
		const reason = applyListOperation(document, items, operation, policy);
		if (reason) skipped.push({ operation: index, reason });
	});
	return skipped;
}

function applyListOperation(
	document: StoryEditDocument,
	items: unknown[],
	operation: StoryListOperation,
	policy: Policy
): StoryEditSkipReason | undefined {
	if (operation.op === 'insert') {
		if (policy === 'tolerant' && isExistingItem(document, items, operation.value)) return 'item_exists';
		items.splice(indexAfter(items, operation.after, policy), 0, structuredClone(operation.value));
	} else if (operation.op === 'move') {
		const source = operation.from ? resolveList(document, operation.from) : items;
		const sourceIndex = source ? indexOf(source, operation.key) : -1;
		if (!source || sourceIndex < 0) {
			reject(policy, source ? `List item ${operation.key} not found` : 'Source list not found');
			return 'item_missing';
		}
		const [item] = source.splice(sourceIndex, 1);
		items.splice(indexAfter(items, operation.after, policy), 0, item);
	} else {
		const index = indexOf(items, operation.key);
		if (index < 0) reject(policy, `List item ${operation.key} not found`);
		else items.splice(index, 1);
	}
	return undefined;
}

const indexOf = (items: unknown[], key: string) => items.findIndex(item => listItemKey(item) === key);

/** Index to insert at after the `after` item; a missing anchor appends when tolerant. */
function indexAfter(items: unknown[], after: string | null, policy: Policy): number {
	if (after === null) return 0;
	const index = indexOf(items, after);
	if (index >= 0) return index + 1;
	reject(policy, `List item ${after} not found`);
	return items.length;
}

function resolveList(document: StoryEditDocument, { target, list }: StoryEditListAddress): unknown[] | undefined {
	const items = resolveTarget(document, target)?.[list];
	return Array.isArray(items) ? items : undefined;
}

const arrayOf = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

function resolveTarget(document: StoryEditDocument, target: StoryEditTarget): Model | undefined {
	const { data } = document;
	const steps = arrayOf(data.steps);
	let model: unknown;
	switch (target.kind) {
		case 'story':
			model = document.story;
			break;
		case 'settings':
			model = document.settings;
			break;
		case 'data':
			model = data;
			break;
		case 'step':
			model = steps.find(step => listItemKey(step) === target.stepId);
			break;
		case 'card':
			model = steps
				.flatMap(step => arrayOf(isRecord(step) ? step.cards : []))
				.find(card => listItemKey(card) === target.cardId);
			break;
		default:
			model = resolveElement(document, target.owner, target.elementId);
	}
	return isRecord(model) ? model : undefined;
}

/** Depth-first search for a block by `_id` among blocks and their children. */
function findBlock(blocks: unknown[], id: string): Model | undefined {
	for (const block of blocks) {
		if (!isRecord(block)) continue;
		if (block._id === id) return block;
		const found = findBlock(arrayOf(block.children), id);
		if (found) return found;
	}
	return undefined;
}

/** Index path of a block among the master's descendants. */
function pathOf(blocks: unknown[], id: string): number[] | undefined {
	for (let index = 0; index < blocks.length; index += 1) {
		const block = blocks[index];
		if (!isRecord(block)) continue;
		if (block._id === id) return [index];
		const path = pathOf(arrayOf(block.children), id);
		if (path) return [index, ...path];
	}
	return undefined;
}

function resolveElement(document: StoryEditDocument, owner: StoryEditElementOwner, id: string): unknown {
	const { data } = document;
	if (owner.kind === 'story') return findBlock(arrayOf(data.elements), id);
	if (owner.kind === 'card') {
		const card = resolveTarget(document, { kind: 'card', cardId: owner.cardId });
		return card && findBlock(arrayOf(card.elements), id);
	}
	const symbol = isRecord(data.symbols) ? data.symbols[owner.symbolId] : undefined;
	if (!isRecord(symbol) || !isRecord(symbol.master)) return undefined;
	const { master } = symbol;
	if (owner.kind === 'symbol-master') return master._id === id ? master : findBlock(arrayOf(master.children), id);

	// An instance override block: its own `_id`, or the master block at the same position (created on demand).
	const instances = isRecord(symbol.instance) ? symbol.instance : undefined;
	const override = instances?.[owner.instanceId];
	if (!isRecord(override)) return undefined;
	if (override._id === id) return override;
	const own = findBlock(arrayOf(override.children), id);
	if (own) return own;
	const path = pathOf(arrayOf(master.children), id);
	if (!path) return undefined;
	let node: Model = override;
	for (const index of path) {
		if (!Array.isArray(node.children)) node.children = [];
		const children = node.children as unknown[];
		if (!isRecord(children[index])) children[index] = {};
		node = children[index] as Model;
	}
	return node;
}
